/**
 * Lives inside the Canvas. Writes frame timing and renderer counters into `perf`
 * every frame; the HUD (outside the Canvas) samples it a few times a second.
 */
import { useEffect, useRef } from 'react'
import { useFrame, useThree } from '@react-three/fiber/webgpu'
import { perf } from './perf'

export function PerfProbe() {
  const acc = useRef({ frames: 0, elapsed: 0, worst: 0, last: performance.now() })
  const renderer = useThree((s) => s.renderer)

  // WebGPURenderer's animation loop zeroes info at frame start, before useFrame runs,
  // so auto-reset would always read 0. Read last frame's totals, then reset ourselves.
  useEffect(() => {
    renderer.info.autoReset = false
    return () => {
      renderer.info.autoReset = true
    }
  }, [renderer])

  // GPU time via timestamp queries (renderer created with trackTimestamp when the HUD is on).
  // Rough load indicator only: below the 60 fps cap, laptop/phone GPUs clock down, so a
  // heavier setup can read lower (seen on every device, 3 Nov). For costs, compare fps
  // under GPU-bound load. Note R3F v10 doesn't run three's animation loop, so
  // info.frame never advances; three's pool still yields roughly one frame per resolve
  // because query uids repeat each frame and overwrite. WebGPU needs the 'timestamp-query' feature; WebGL 2 needs
  // EXT_disjoint_timer_query_webgl2 (mostly absent). Without them the HUD shows n/a.
  //
  // resolveTimestampsAsync() returns the GPU time of the LAST frame among those pending,
  // not their sum (three r186, WebGPUTimestampQueryPool). So each result is one frame's
  // sample: average the samples. (3 Nov bug: dividing by frames-since-last-resolve made
  // heavier setups, whose resolves span more frames, read lower. Post-on looked cheaper
  // than post-off on every device.)
  const gpu = useRef({ inFlight: false, ms: 0, samples: 0, windowStart: performance.now() })

  useFrame((state) => {
    const now = performance.now()
    const g = gpu.current
    const backend = state.renderer.backend as { trackTimestamp?: boolean }
    if (backend.trackTimestamp) {
      if (!g.inFlight) {
        g.inFlight = true
        state.renderer
          .resolveTimestampsAsync()
          .then((d) => {
            if (typeof d === 'number' && d > 0) {
              g.ms += d
              g.samples++
            }
          })
          .catch(() => {})
          .finally(() => {
            g.inFlight = false
          })
      }
      if (now - g.windowStart >= 1000) {
        if (g.samples > 0) perf.gpuMs = g.ms / g.samples
        g.ms = 0
        g.samples = 0
        g.windowStart = now
      }
    }

    const a = acc.current
    const dt = now - a.last
    a.last = now
    a.frames++
    a.elapsed += dt
    if (dt > a.worst) a.worst = dt

    if (a.elapsed >= 1000) {
      perf.fps = (a.frames * 1000) / a.elapsed
      perf.frameMs = a.elapsed / a.frames
      perf.worstMs = a.worst
      a.frames = 0
      a.elapsed = 0
      a.worst = 0
    }

    const info = state.renderer.info
    perf.drawCalls = info.render.drawCalls
    perf.triangles = info.render.triangles
    info.reset()
    perf.dpr = state.viewport.dpr
    perf.width = Math.round(state.size.width)
    perf.height = Math.round(state.size.height)
  })

  return null
}
